import { useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Plus, Trash2, CheckCircle2, AlertTriangle, XCircle, Send, Info, ChevronDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";

// Colonne = campi realmente usati dalla procedura "Nuovo lavoratore" (POST /workers).
// L'ordine dei campi testuali definisce la mappatura del copia/incolla da Excel/Sheets.
// L'appalto NON è gestito via incolla: solo multi-select riga + multi-select globale.
const TEXT_COLS = [
  { key: "nome", label: "Nome", w: "min-w-[130px]" },
  { key: "cognome", label: "Cognome", w: "min-w-[130px]" },
  { key: "ruolo_professionale", label: "Ruolo professionale", w: "min-w-[160px]" },
  { key: "email", label: "Email", w: "min-w-[220px]" },
  { key: "monte_ore", label: "Monte ore sett.", w: "min-w-[120px]" },
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseHours = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const emptyRow = (appaltoIds) => ({
  nome: "", cognome: "", ruolo_professionale: "", email: "", monte_ore: "",
  appalto_ids: [...(appaltoIds || [])], status: "", error: "",
});

const isEmptyRow = (r) => !r.nome.trim() && !r.cognome.trim() && !r.ruolo_professionale.trim() && !r.email.trim() && !r.monte_ore.trim();

// Multi-select con chip, coerente con la UI Orbit (Popover + checkbox).
export function AppaltiPicker({ value, onChange, appalti, placeholder, disabled, testid }) {
  const [open, setOpen] = useState(false);
  const byId = (aid) => appalti.find((a) => a.id === aid);
  const toggle = (aid) => onChange(value.includes(aid) ? value.filter((x) => x !== aid) : [...value, aid]);
  const remove = (aid) => onChange(value.filter((x) => x !== aid));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} data-testid={testid}
          className={`w-full min-h-9 px-2 py-1 rounded-md border text-left text-sm bg-white flex flex-wrap gap-1 items-center transition-colors ${disabled ? "opacity-60" : "hover:border-primary/40"} border-slate-200`}>
          {value.length === 0 ? (
            <span className="text-slate-400">{placeholder}</span>
          ) : value.map((aid) => {
            const a = byId(aid); if (!a) return null;
            return (
              <span key={aid} className="inline-flex items-center gap-1 rounded-full bg-[#EDEAF6] border border-[#D6D0EA] text-primary text-xs font-medium px-2 py-0.5" data-testid={`${testid}-chip-${aid}`}>
                {a.nome}
                {!disabled && (
                  <span role="button" data-testid={`${testid}-remove-${aid}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(aid); }}
                    className="hover:text-destructive"><X className="h-3 w-3" /></span>
                )}
              </span>
            );
          })}
          <ChevronDown className="h-4 w-4 text-slate-400 ml-auto shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-1 w-60" align="start">
        {appalti.length === 0 ? (
          <p className="text-sm text-slate-500 p-2">Nessun appalto disponibile nel tuo perimetro.</p>
        ) : appalti.map((a) => (
          <label key={a.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer text-sm">
            <Checkbox checked={value.includes(a.id)} data-testid={`${testid}-opt-${a.id}`} onCheckedChange={() => toggle(a.id)} />
            <span className="text-slate-700">{a.nome}</span>
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function MultiWorkerImport({ appalti = [], existingEmails = [], onClose }) {
  const defAppalti = appalti.length === 1 ? [appalti[0].id] : [];
  const [globalAppalti, setGlobalAppalti] = useState([]);
  const [rows, setRows] = useState(() => Array.from({ length: 5 }, () => emptyRow(defAppalti)));
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {created, exists, error}
  const existingSet = useMemo(() => new Set(existingEmails.map((e) => (e || "").toLowerCase().trim())), [existingEmails]);

  // Validazione riga (rispecchia i controlli del flusso singolo + duplicati nel batch)
  const rowErrors = (r, idx) => {
    if (r.status === "created") return {};
    if (isEmptyRow(r)) return {};
    const errs = {};
    if (!r.nome.trim()) errs.nome = "Obbligatorio";
    if (!r.cognome.trim()) errs.cognome = "Obbligatorio";
    if (!r.ruolo_professionale.trim()) errs.ruolo_professionale = "Obbligatorio";
    const email = r.email.trim().toLowerCase();
    if (!email) errs.email = "Obbligatorio";
    else if (!EMAIL_RE.test(email)) errs.email = "Email non valida";
    else if (existingSet.has(email)) errs.email = "Email già presente";
    else if (rows.some((o, i) => i !== idx && o.email.trim().toLowerCase() === email)) errs.email = "Email duplicata nella tabella";
    const mo = parseHours(r.monte_ore);
    if (!(mo && mo > 0)) errs.monte_ore = "Monte ore > 0 obbligatorio";
    if (!r.appalto_ids || r.appalto_ids.length === 0) errs.appalto = "Seleziona almeno un appalto";
    return errs;
  };

  const errorsByRow = rows.map((r, i) => rowErrors(r, i));
  const entered = rows.filter((r) => !isEmptyRow(r)).length;
  const createdCount = rows.filter((r) => r.status === "created").length;
  const readyIdx = rows.map((r, i) => ({ r, i, e: errorsByRow[i] }))
    .filter(({ r, e }) => !isEmptyRow(r) && r.status !== "created" && Object.keys(e).length === 0)
    .map(({ i }) => i);
  const ready = readyIdx.length;
  const toFix = rows.filter((r, i) => !isEmptyRow(r) && r.status !== "created" && Object.keys(errorsByRow[i]).length > 0).length;

  const touch = (row) => ({ ...row, status: row.status === "created" ? row.status : "", error: "" });
  const setCell = (idx, key, value) => {
    setRows((prev) => prev.map((row, i) => (i === idx ? { ...touch(row), [key]: value } : row)));
    setResult(null);
  };
  const setRowAppalti = (idx, ids) => {
    setRows((prev) => prev.map((row, i) => (i === idx ? { ...touch(row), appalto_ids: ids } : row)));
    setResult(null);
  };

  const applyAll = () => {
    setRows((prev) => prev.map((row) => (row.status === "created" ? row : { ...touch(row), appalto_ids: [...globalAppalti] })));
    setResult(null);
    toast.success(`Appalti applicati a tutte le righe`);
  };

  const addRow = () => { setRows((prev) => [...prev, emptyRow(globalAppalti.length ? globalAppalti : defAppalti)]); setResult(null); };
  const deleteRow = (idx) => { setRows((prev) => (prev.length <= 1 ? [emptyRow(globalAppalti.length ? globalAppalti : defAppalti)] : prev.filter((_, i) => i !== idx))); setResult(null); };

  // Copia/incolla: solo campi testuali (appalto escluso, come da requisito)
  const handlePaste = (e, r, c) => {
    const text = e.clipboardData?.getData("text");
    if (!text) return;
    e.preventDefault();
    let lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
    while (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
    const parsed = lines.map((l) => l.split("\t"));
    setRows((prev) => {
      const next = prev.map((row) => ({ ...row }));
      const needed = r + parsed.length;
      while (next.length < needed) next.push(emptyRow(globalAppalti.length ? globalAppalti : defAppalti));
      parsed.forEach((cells, ri) => {
        cells.forEach((val, ci) => {
          const colIdx = c + ci;
          if (colIdx >= TEXT_COLS.length) return; // colonne oltre i campi testuali (es. appalto) ignorate
          if (next[r + ri].status === "created") return;
          next[r + ri][TEXT_COLS[colIdx].key] = (val ?? "").trim();
          next[r + ri].status = ""; next[r + ri].error = "";
        });
      });
      return next;
    });
    setResult(null);
  };

  const focusCell = (r, c) => {
    const el = document.querySelector(`[data-cell="${r}-${c}"]`);
    if (el) { el.focus(); if (el.select) el.select(); }
  };
  const handleKeyDown = (e, r, c) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (r === rows.length - 1) { addRow(); setTimeout(() => focusCell(r + 1, c), 30); }
      else focusCell(r + 1, c);
    } else if (e.key === "ArrowDown" && !e.shiftKey && r < rows.length - 1) { e.preventDefault(); focusCell(r + 1, c); }
    else if (e.key === "ArrowUp" && !e.shiftKey && r > 0) { e.preventDefault(); focusCell(r - 1, c); }
  };

  const submit = async () => {
    if (ready === 0 || submitting) return;
    setSubmitting(true);
    let created = 0, exists = 0, error = 0;
    const updates = {};
    for (const idx of readyIdx) {
      const r = rows[idx];
      try {
        await api.post("/workers", {
          nome: r.nome.trim(), cognome: r.cognome.trim(), ruolo_professionale: r.ruolo_professionale.trim(),
          email: r.email.trim(), profilo_accesso: "LAVORATORE",
          monte_ore_settimanale_contrattuale: parseHours(r.monte_ore),
          appalto_ids: r.appalto_ids,
        });
        updates[idx] = { status: "created", error: "" }; created += 1;
      } catch (err) {
        const detail = formatApiError(err.response?.data?.detail) || err.message || "Errore";
        if (/gi[àa] (un account|presente)|esiste gi/i.test(detail)) { updates[idx] = { status: "exists", error: detail }; exists += 1; }
        else { updates[idx] = { status: "error", error: detail }; error += 1; }
      }
    }
    setRows((prev) => prev.map((row, i) => (updates[i] ? { ...row, ...updates[i] } : row)));
    setResult({ created, exists, error });
    setSubmitting(false);
    if (created > 0) toast.success(`${created} ${created === 1 ? "lavoratore creato e invitato" : "lavoratori creati e invitati"}`);
    if (error > 0) toast.error(`${error} ${error === 1 ? "riga non creata" : "righe non create"}`);
  };

  const statusBadge = (r) => {
    if (r.status === "created") return <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid="row-status-created"><CheckCircle2 className="h-3.5 w-3.5" />Creato</span>;
    if (r.status === "exists") return <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600" data-testid="row-status-exists"><AlertTriangle className="h-3.5 w-3.5" />Già presente</span>;
    if (r.status === "error") return <span className="inline-flex items-center gap-1 text-xs font-semibold text-destructive" data-testid="row-status-error"><XCircle className="h-3.5 w-3.5" />Errore</span>;
    return null;
  };

  return (
    <div data-testid="multi-worker-import">
      <button data-testid="multi-back-btn" onClick={onClose}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline mb-4">
        <ArrowLeft className="h-4 w-4" /> Torna ai Lavoratori
      </button>

      <h2 className="font-heading text-xl font-bold text-slate-900 mb-1">Inserimento multiplo lavoratori</h2>
      <p className="text-sm text-slate-500 mb-4">Compila la tabella o incolla direttamente da Excel / Google Sheets. Ogni riga valida crea un account e invia l'invito, esattamente come "Nuovo lavoratore".</p>

      <div className="mb-4 rounded-lg bg-[#EDEAF6] border border-[#D6D0EA] text-slate-700 text-sm px-3 py-2 flex items-start gap-2" data-testid="multi-paste-hint">
        <Info className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
        <span>Suggerimento: seleziona la prima cella e incolla (Ctrl/Cmd+V) più righe da un foglio di calcolo. Colonne attese in ordine: Nome, Cognome, Ruolo, Email, Monte ore. Gli appalti si impostano dai menu a tendina.</span>
      </div>

      {/* Appalto per tutti */}
      <div className="mb-4 flex flex-wrap items-end gap-3" data-testid="multi-global-appalti-row">
        <div className="min-w-[260px]">
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Appalto per tutti</label>
          <AppaltiPicker value={globalAppalti} onChange={setGlobalAppalti} appalti={appalti} placeholder="Seleziona uno o più appalti…" testid="multi-global-appalti" />
        </div>
        <Button variant="outline" size="sm" data-testid="multi-apply-all-btn" disabled={globalAppalti.length === 0} onClick={applyAll}
          className="rounded-lg bg-white text-slate-700 border-slate-300 hover:bg-slate-50">
          Applica a tutte le righe
        </Button>
      </div>

      {/* Riepilogo conteggi */}
      <div className="mb-3 flex flex-wrap items-center gap-4 text-sm" data-testid="multi-summary">
        <span className="font-semibold text-slate-700">{entered} inseriti</span>
        <span className="text-emerald-600 font-semibold" data-testid="multi-ready-count">{ready} pronti per l'invito</span>
        {toFix > 0 && <span className="text-amber-600 font-semibold" data-testid="multi-tofix-count">{toFix} da correggere</span>}
        {createdCount > 0 && <span className="text-slate-500">{createdCount} già creati</span>}
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-[#F1EEF9] text-slate-600">
              <th className="px-2 py-2 text-left font-semibold w-10">#</th>
              {TEXT_COLS.map((col) => (
                <th key={col.key} className={`px-2 py-2 text-left font-semibold ${col.w}`}>{col.label}<span className="text-destructive"> *</span></th>
              ))}
              <th className="px-2 py-2 text-left font-semibold min-w-[200px]">Appalto<span className="text-destructive"> *</span></th>
              <th className="px-2 py-2 text-left font-semibold min-w-[120px]">Stato</th>
              <th className="px-2 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => {
              const errs = errorsByRow[r];
              const locked = row.status === "created";
              return (
                <tr key={r} className={`border-t border-slate-100 ${locked ? "bg-emerald-50/40" : row.status === "error" || row.status === "exists" ? "bg-amber-50/30" : ""}`} data-testid={`multi-row-${r}`}>
                  <td className="px-2 py-1 text-slate-400 text-xs text-center">{r + 1}</td>
                  {TEXT_COLS.map((col, c) => (
                    <td key={col.key} className="px-1 py-1 align-top">
                      <input
                        data-cell={`${r}-${c}`}
                        data-testid={`multi-cell-${r}-${col.key}`}
                        disabled={locked}
                        value={row[col.key]}
                        onChange={(e) => setCell(r, col.key, e.target.value)}
                        onPaste={(e) => handlePaste(e, r, c)}
                        onKeyDown={(e) => handleKeyDown(e, r, c)}
                        className={`w-full h-9 px-2 rounded-md border text-sm bg-white outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary ${errs[col.key] ? "border-destructive bg-destructive/5" : "border-slate-200"} ${locked ? "text-slate-400" : "text-slate-800"}`}
                        title={errs[col.key] || ""}
                      />
                      {errs[col.key] && <p className="text-[11px] text-destructive mt-0.5 px-1" data-testid={`multi-err-${r}-${col.key}`}>{errs[col.key]}</p>}
                    </td>
                  ))}
                  <td className="px-1 py-1 align-top min-w-[200px]">
                    <AppaltiPicker value={row.appalto_ids} onChange={(ids) => setRowAppalti(r, ids)} appalti={appalti}
                      placeholder="Seleziona…" disabled={locked} testid={`multi-cell-${r}-appalto`} />
                    {errs.appalto && <p className="text-[11px] text-destructive mt-0.5 px-1" data-testid={`multi-err-${r}-appalto`}>{errs.appalto}</p>}
                  </td>
                  <td className="px-2 py-1 align-middle">
                    {statusBadge(row)}
                    {row.error && (row.status === "error" || row.status === "exists") && <p className="text-[11px] text-slate-500 mt-0.5 max-w-[160px]">{row.error}</p>}
                  </td>
                  <td className="px-1 py-1 text-center align-middle">
                    <button data-testid={`multi-delete-row-${r}`} onClick={() => deleteRow(r)}
                      className="text-slate-400 hover:text-destructive transition-colors p-1" title="Elimina riga">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3">
        <Button variant="outline" size="sm" data-testid="multi-add-row-btn" onClick={addRow} className="rounded-lg">
          <Plus className="h-4 w-4 mr-1.5" /> Aggiungi riga
        </Button>
      </div>

      {result && (
        <div className="mt-5 rounded-xl border border-slate-200 bg-white shadow-sm p-4" data-testid="multi-result-banner">
          <p className="font-heading font-bold text-slate-900 mb-2">Inserimento completato</p>
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5 text-emerald-600 font-semibold"><CheckCircle2 className="h-4 w-4" />{result.created} creati e invitati</span>
            {result.exists > 0 && <span className="inline-flex items-center gap-1.5 text-amber-600 font-semibold"><AlertTriangle className="h-4 w-4" />{result.exists} già presenti</span>}
            {result.error > 0 && <span className="inline-flex items-center gap-1.5 text-destructive font-semibold"><XCircle className="h-4 w-4" />{result.error} non creati</span>}
          </div>
          {(result.exists > 0 || result.error > 0) && <p className="text-xs text-slate-500 mt-2">Le righe non completate restano modificabili: correggile e premi di nuovo "Crea e invia inviti". I lavoratori già creati non verranno reinviati.</p>}
        </div>
      )}

      <div className="mt-6 sticky bottom-0 bg-gradient-to-t from-white via-white to-transparent pt-4 pb-2 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-slate-700" data-testid="multi-ready-label">{ready} {ready === 1 ? "lavoratore pronto" : "lavoratori pronti"}</p>
        <Button data-testid="multi-submit-btn" onClick={submit} disabled={ready === 0 || submitting}
          className="rounded-lg font-semibold">
          <Send className="h-4 w-4 mr-2" />{submitting ? "Invio in corso…" : `Crea e invia ${ready} ${ready === 1 ? "invito" : "inviti"}`}
        </Button>
      </div>
    </div>
  );
}
